/**
 * Context-product exclusion for dynamic offer types (storefront + extensions).
 * Keep in sync with weupsell-ai/shared/products-to-offer-context.js
 */

/** Offer types that must not return the PDP / cart context product. */
export const OFFER_TYPES_EXCLUDING_CONTEXT_PRODUCT = new Set([
  "product_from_collection",
  "shopify_recommended",
  "ai_recommended",
  "top_selling_products",
  "realtime_same_collection",
  "realtime_same_vendor",
  "realtime_same_tags",
  "realtime_title_contains",
  "product_specific_tag",
  "product_specific_vendor",
]);

export function shouldExcludeContextProductFromOffer(offerType) {
  return OFFER_TYPES_EXCLUDING_CONTEXT_PRODUCT.has(String(offerType || "").trim());
}

export function normalizeShopifyProductId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.includes("/") ? raw.split("/").pop() : raw;
}

export function mergeExcludeProductIds(existing, ...idLists) {
  const set = new Set();
  const addFrom = (source) => {
    if (source == null || source === "") return;
    const list = Array.isArray(source)
      ? source
      : String(source)
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
    for (const id of list) {
      const normalized = normalizeShopifyProductId(id);
      if (normalized) set.add(normalized);
    }
  };

  addFrom(existing);
  for (const list of idLists) {
    addFrom(list);
  }

  return [...set];
}

/**
 * @param {Record<string, string>} params
 * @param {string} offerType
 * @param {string | number | Array<string | number>} contextProductIds
 */
export function buildContextProductFromCartItem(item) {
  if (!item || item.product_id == null) return null;
  return {
    id: item.product_id,
    title: item.product_title || item.title || "",
    handle: item.handle || "",
    vendor: item.vendor || "",
    tags: item.tags || item.product_tags || [],
  };
}

export function shouldPreferCartContextForOffer(win, options = {}) {
  if (options.preferCart === true) return true;
  const surface = String(options.surface || "").trim().toLowerCase();
  if (surface === "cart-drawer" || surface === "cart_drawer") return true;
  return win?.__WEUPSELL_STATIC_CART_DRAWER_OPEN__ === true;
}

/**
 * Context product for offer resolution.
 * Cart drawer surfaces prefer the latest cart line; PDP popups prefer page product.
 */
export function resolveOfferContextProduct(
  win = typeof window !== "undefined" ? window : null,
  options = {}
) {
  if (!win) return null;

  const preferCart = shouldPreferCartContextForOffer(win, options);
  const items = win.__weupsellCart?.items;
  const cartProduct =
    Array.isArray(items) && items.length
      ? buildContextProductFromCartItem(items[items.length - 1])
      : null;
  const pdp = win.__weupsellProduct;

  if (preferCart && cartProduct) return cartProduct;
  if (pdp && pdp.id != null) return pdp;
  return cartProduct;
}

export function appendContextProductExclusionToRandomApiParams(
  params,
  offerType,
  contextProductIds
) {
  if (!params || !shouldExcludeContextProductFromOffer(offerType)) {
    return params;
  }

  const ids = mergeExcludeProductIds(
    params.excludeProductIds,
    contextProductIds
  );

  if (!ids.length) {
    return params;
  }

  return { ...params, excludeProductIds: ids.join(",") };
}
